import React from "react";
import { Row, Col } from "react-bootstrap";
import { object, string, func, bool, number } from "prop-types";
import { format, parseISO } from "date-fns";
import { getTestClassName } from "utils/helperFunctions";
import {
  microsoftTestIdToName,
  subTestIdToSubTestName,
  addDashesToEventID,
} from "containers/Results/FunctionLib";
import PendingEventsButtons from "./PendingEventsButtons";

const PendingEventsRow = ({
  allUsers,
  candidate,
  candidateWillNotBeReminded,
  currentCandidateId,
  deleteInvitedCandidate,
  event,
  fromInvited,
  fromResultsV2,
  jobData,
  moveEmailCandidatesPermission,
  onPendingEventsActionCompletion,
  onShowExtendExpirationModal,
  permissionScoreReports,
  showReleaseScoresModal,
  showReminderErrorModal,
  showReminderModal,
  showVideoInterviewAdjustmentsModal,
  testEvent,
  updateExtendExpirationCandidateIds,
  updateExtendExpirationTestEventIds,
}) => {
  /* --------- Setting UI ---------- */
  const pendingEvaluationToolBars = [];

  const generateEvaluationToolBar = (
    evalToolId,
    isCompleted,
    testClassName,
    displayName
  ) => (
    <div className="mr-2 mb-2 float-left" key={evalToolId}>
      <table
        className="table table-borderless table-sm candidateCardTable"
        style={{ width: "180px" }}
      >
        <tbody>
          <tr>
            <td
              className={`align-middle text-left ${
                isCompleted ? "" : "incomplete-event"
              }`}
              style={{ height: "24px" }}
            >
              <h6 className={testClassName}>{displayName}</h6>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  if (event.tests) {
    event.tests.map((test) => {
      let testName = test.get("testName")
        ? test.get("testName").slice(0, 7)
        : "";
      testName += test.get("testName").length > 7 ? "..." : "";
      let subTestName =
        test.get("subTestId") && subTestIdToSubTestName[test.get("subTestId")]
          ? subTestIdToSubTestName[test.get("subTestId")]
          : null;
      subTestName =
        subTestName && subTestName.length > 7
          ? `${subTestName.slice(0, 7)}...`
          : subTestName;
      if (microsoftTestIdToName[test.get("testId")]) {
        testName = microsoftTestIdToName[test.get("testId")];
      }
      const testClassName = getTestClassName(
        `test-card-test-name`,
        test.get("testId")
      );

      const TestBar = generateEvaluationToolBar(
        `${testEvent}-assessment-${test.get("testId")}`,
        test.get("finished"),
        testClassName,
        subTestName || testName
      );
      pendingEvaluationToolBars.push(TestBar);
    });
  }

  if (event.videoInterviews) {
    const testClassName = getTestClassName("test-card-test-name", null, true);
    event.videoInterviews.map((videoInterview) => {
      const TestBar = generateEvaluationToolBar(
        `${testEvent}-videoInterview-${videoInterview.get("videoInterviewId")}`,
        videoInterview.get("dateCompleted"),
        testClassName,
        "Video Interview"
      );
      pendingEvaluationToolBars.push(TestBar);
    });
  }

  if (event.liveInterviews) {
    const testClassName = getTestClassName("test-card-test-name", null, true);
    event.liveInterviews.map((liveInterview) => {
      const TestBar = generateEvaluationToolBar(
        `${testEvent}-liveInterview-${liveInterview.get("liveInterviewId")}`,
        liveInterview.get("dateCompleted"),
        testClassName,
        "Live Interview"
      );
      pendingEvaluationToolBars.push(TestBar);
    });
  }

  return (
    <Row key={testEvent} className="pending-event-testevent-row">
      <Col lg={3} md={3} sm={3} className="align-self-center">
        <p className="pending-events-eventid-text">
          {addDashesToEventID(event.eventId)}
        </p>
        <p className="pending-events-text">
          {event.date && format(parseISO(event.date), "MMM dd, yyyy")}
        </p>
        <p className="pending-events-text">
          Scheduled By:{" "}
          {allUsers.size > 0 && allUsers.get(String(event.scheduledBy))
            ? allUsers.get(String(event.scheduledBy)).get("fullName")
            : ""}
        </p>
      </Col>
      {permissionScoreReports ? (
        <Col lg={8} md={8} sm={8} className="align-self-center">
          {pendingEvaluationToolBars}
        </Col>
      ) : (
        <Col lg={8} md={8} sm={8}></Col>
      )}
      <Col lg={1} md={1} sm={1} className="text-right align-self-center">
        <PendingEventsButtons
          candidate={candidate}
          candidateWillNotBeReminded={candidateWillNotBeReminded}
          currentCandidateId={currentCandidateId}
          deleteInvitedCandidate={deleteInvitedCandidate}
          eventId={event.eventId}
          fromInvited={fromInvited}
          fromResultsV2={fromResultsV2}
          jobData={jobData}
          key={testEvent}
          moveEmailCandidatesPermission={moveEmailCandidatesPermission}
          onPendingEventsActionCompletion={onPendingEventsActionCompletion}
          onShowExtendExpirationModal={onShowExtendExpirationModal}
          showVideoInterviewAdjustmentsModal={
            showVideoInterviewAdjustmentsModal
          }
          showReleaseScoresModal={showReleaseScoresModal}
          showReminderErrorModal={showReminderErrorModal}
          showReminderModal={showReminderModal}
          testEventId={testEvent}
          tests={event.tests}
          updateExtendExpirationCandidateIds={
            updateExtendExpirationCandidateIds
          }
          updateExtendExpirationTestEventIds={
            updateExtendExpirationTestEventIds
          }
          isLiveInterviewEvent={Boolean(event.liveInterviews)}
        />
      </Col>
    </Row>
  );
};

PendingEventsRow.propTypes = {
  allUsers: object,
  candidate: object,
  candidateWillNotBeReminded: bool,
  currentCandidateId: string,
  deleteInvitedCandidate: func.isRequired,
  event: object,
  fromInvited: bool,
  fromResultsV2: bool,
  jobData: object,
  moveEmailCandidatesPermission: bool,
  onPendingEventsActionCompletion: func.isRequired,
  onShowExtendExpirationModal: func.isRequired,
  permissionScoreReports: number,
  showReleaseScoresModal: func,
  showReminderErrorModal: func.isRequired,
  showReminderModal: func.isRequired,
  showVideoInterviewAdjustmentsModal: func,
  testEvent: string,
  updateExtendExpirationCandidateIds: func.isRequired,
  updateExtendExpirationTestEventIds: func.isRequired,
};

export default PendingEventsRow;
